import { motion } from "framer-motion";
import { useContext, useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../../api/api';
import { UserContext } from '../../context/UserContext';
import { routes, getDefaultRouteForRoles } from '../../config/utility';
import { toast } from 'react-toastify';
import ThemeButton from "../../components/atoms/ThemeButton";
import SecondaryThemeButton from "../../components/atoms/SecondaryThemeButton";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const VerifyOtp = () => {
  const { contextLogin, contextLogout, clearUserData } = useContext(UserContext);
  const { state } = useLocation();
  const [otp, setOtp] = useState(new Array(6).fill(''));
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const navigate = useNavigate();
  const inputRefs = useRef([]);

  useEffect(() => {
    if (!state || !state.email) {
      navigate(routes.registration);
    }
  }, [state, navigate]);

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const otpCode = otp.join('');
      const response = await api.post('/verify-otp', { email: state?.email, otp: otpCode });
      contextLogin(response.data);
      navigate(getDefaultRouteForRoles(response.data.user?.role ?? []));
    } catch (error) {
      console.error('OTP verification error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setResendLoading(true);
    try {
      await api.post('/resend-otp', { email: state?.email });
      toast.success('OTP sent successfully!');
    } catch (error) {
      console.error('Error resending OTP:', error);
      toast.error('Failed to resend OTP. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  const handleChange = (value, index) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleBack = async () => {
    try {
      setLoading(true);
      contextLogout();
      // Clear user data after 1 second
      setTimeout(() => {
        clearUserData();
        navigate(routes.login, { replace: true });
      }, 500);
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Failed to logout. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 100 }
    }
  };

  return (
    <motion.div
      className="flex h-screen w-screen overflow-hidden bg-body-auth"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="w-full flex items-center justify-center relative backdrop-blur-lg">
        {/* Back Button - Added this section */}
        <motion.button
          className="absolute top-8 left-8 z-20 flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          onClick={handleBack}
          initial={{ x: -20, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <FontAwesomeIcon icon={faArrowLeft} className="text-xl" />
          <span className="text-sm font-medium">Back to Login</span>
        </motion.button>

        {/* Top Image Panel */}
        <motion.div
          className="absolute top-4 sm:-top-10 left-30 max-w-lg z-10"
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ type: "spring", duration: 0.8 }}
        >
          <img
            src="/assets/kids-header4.png"
            alt=""
            className="w-full h-full object-cover object-bottom"
          />
        </motion.div>

        {/* Verify OTP Card */}
        <motion.div
          className="sm:h-[calc(100vh-47vh)] flex flex-col justify-evenly relative w-full max-w-2xl mx-auto bg-white/85 border border-white/20 rounded-2xl p-8 backdrop-blur-md shadow-xl sm:mt-32"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Header */}
          <motion.div
            className="text-center flex flex-col items-center justify-center"
            variants={itemVariants}
          >
            <motion.img
              src="/assets/FULLLOGO.svg"
              alt="Popout"
              className="w-64 mb-8"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", duration: 0.8 }}
            />
            <motion.h2
              className="text-2xl font-display font-bold text-dark"
              variants={itemVariants}
            >
              Verify Your Email
            </motion.h2>
            <motion.p
              className="text-grey"
              variants={itemVariants}
            >
              Enter the 6-digit code sent to your email: <strong>{state?.email}</strong>
            </motion.p>
          </motion.div>

          <form onSubmit={handleVerify} className="space-y-8">
            {/* OTP Input */}
            <motion.div
              className="flex justify-center gap-2 sm:gap-4 px-2 sm:px-0"
              variants={itemVariants}
            >
              {otp.map((digit, index) => (
                <input
                  key={index}
                  type="text"
                  maxLength="1"
                  value={digit}
                  onChange={(e) => handleChange(e.target.value, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  ref={(el) => (inputRefs.current[index] = el)}
                  className="w-11 h-11 sm:w-14 sm:h-14 text-center text-base sm:text-xl font-semibold bg-primary/10 
                           border-2 border-gray-200 rounded-lg
                           transition-all duration-300 box-border"
                  style={{ 
                    padding: '0',
                    lineHeight: '1',
                    WebkitAppearance: 'none',
                    MozAppearance: 'textfield'
                  }}
                />
              ))}
            </motion.div>

            {/* Actions */}
            <div className="space-y-2">
              {/* Primary Action */}
              <motion.div
                variants={itemVariants}
                className="flex justify-center items-center w-full"
              >
                <ThemeButton
                  type="submit"
                  label={loading ? "Verifying..." : "Verify OTP"}
                  onClick={handleVerify}
                  disabled={loading}
                  className="w-full max-w-xs"
                  primaryColor="bg-secondary"
                  secondaryColor="bg-accentSecondary"
                  textColor="#ffffff"
                  hoverTextColor="#160033"
                />
              </motion.div>

              {/* Secondary Action */}
              <motion.div
                variants={itemVariants}
                className="flex justify-center"
              >
                <SecondaryThemeButton
                  label={resendLoading ? "Sending..." : "Resend OTP"}
                  onClick={handleResendOtp}
                  disabled={resendLoading}
                  className="text-sm"
                />
              </motion.div>
            </div>
          </form>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default VerifyOtp;
